/* ─────────────────────────────────────────────
   HIGH-ENTROPY (private) device minting — ADR 0011 + Spec 2
   A paid/private identity is CSPRNG-minted (non-discoverable cohort,
   non-derivable token). It rides the existing apiCreateDevice path (the
   backend already accepts opaque keys) and is hard-gated on a backup at mint —
   a CSPRNG identity is NOT re-derivable, so it is unrecoverable without one.
   ───────────────────────────────────────────── */
import { randomLabel, randomGroup } from '../../shared/highentropy';
import { showToast } from './toast';
import { getAuthUserId } from '../state/auth-state';
import { rememberDevice } from '../storage/persistence';
import { apiCreateDevice } from '../api/client';
import { claimCurrentDevice } from './account';
import { openBackupExport } from './backup-modal';

/* ─────────────────────────────────────────────
   Callbacks injected by main.ts boot() to avoid circular imports
   ───────────────────────────────────────────── */
let _openDrawerCb: ((view: string) => void) | null = null;
export function setHighEntropyOpenDrawerCb(fn: (view: string) => void): void { _openDrawerCb = fn; }
let _closeDrawerCb: (() => void) | null = null;
export function setHighEntropyCloseDrawerCb(fn: () => void): void { _closeDrawerCb = fn; }
let _activateDeviceCb: ((device: any, restored: boolean) => void) | null = null;
export function setHighEntropyActivateDeviceCb(fn: (device: any, restored: boolean) => void): void { _activateDeviceCb = fn; }

async function mintHighEntropyDevice(group?: string[] | null, opts: { promptName?: boolean; invite?: string } = {}){
  if(getAuthUserId() == null){
    showToast('sign in (Account) to create a private device');
    _openDrawerCb?.('account');
    return;
  }
  const g = (group && group.length === 2) ? group.slice() : randomGroup();
  const label = randomLabel();
  const name = opts.promptName ? (prompt('Name this private device (optional)') || '').trim() : '';
  let device: any;
  try {
    // `opts.invite` is set when joining a CLAIMED group via an invite link;
    // unset for a fresh mint or an open-group join (the server gates accordingly).
    const res = await apiCreateDevice(label, g, opts.invite);
    device = { token: res.token, animals: label, group: g, created_at: res.created_at };
    if(name) device.name = name;
    rememberDevice(device);
  } catch (err) {
    console.warn('[locus] high-entropy create failed', err);
    showToast('could not create private device — are you online?');
    return;
  }
  _closeDrawerCb?.();
  _activateDeviceCb?.(device, /*restored*/ false);
  // Ownership is required to back up; claim automatically (best-effort — the
  // helper surfaces its own errors if the claim can't complete).
  try { await claimCurrentDevice(); } catch { /* surfaced by claimCurrentDevice */ }
  // Hard-gate the backup: open export immediately so the only path forward is
  // to make the unrecoverable identity recoverable.
  showToast('private device created — back it up to keep it');
  openBackupExport();
}

// Open a join link's group: confirm, then mint a private device in it. For a
// legacy `#/join/` link the group key IS the secret (treat it like a password);
// for a `#/join-group/` link an `invite` token is also carried and threaded into
// the create so a CLAIMED group accepts the new device (ADR 0025).
function promptJoin(group: string[], opts: { invite?: string } = {}){
  if(getAuthUserId() == null){
    showToast('sign in to join this private group, then re-open the link');
    _openDrawerCb?.('account');
    return;
  }
  if(confirm(`Join private group "${group[0]}-${group[1]}"?\nThis creates a new private device in that group.`)){
    mintHighEntropyDevice(group, { invite: opts.invite });
  }
}

async function copyText(text: string){
  try { await navigator.clipboard.writeText(text); showToast('copied'); }
  catch { showToast('copy failed — long-press to copy'); }
}

export { mintHighEntropyDevice, promptJoin, copyText };
